import {CampaignTargetType} from '../__generated__/graphql'

export const isValidHttpUrlWithDomain = (url?: string): boolean => {
  if (!url) return false
  try {
    const parsed = new URL(url)
    const isHttp = parsed.protocol === 'http:' || parsed.protocol === 'https:'
    const hasDotDomain = parsed.hostname.includes('.')
    return isHttp && hasDotDomain
  } catch {
    return false
  }
}

export const isValidHttpsUrlWithDomain = (url?: string): boolean => {
  if (!url || !isValidHttpUrlWithDomain(url)) return false
  return new URL(url).protocol === 'https:'
}

export const validateCampaign = ({ landingPage }: { landingPage: string }) => {
  if (!isValidHttpsUrlWithDomain(landingPage)) {
    // eslint-disable-next-line no-throw-literal
    throw [['landingPage', { type: 'manual', message: 'Landing Page must be a valid https:// URL with a domain' }]]
  }
}

const WHITESPACE = /\s/

// True if the URL contains any whitespace. Such URLs pass isValidHttpUrlWithDomain today
// because `new URL()` silently percent-encodes a space in the path/query
// (e.g. "https://example.com/a?a =1" -> ".../a?a%20=1"), so we flag whitespace separately.
export const urlHasSpace = (url?: string): boolean => !!url && WHITESPACE.test(url)

// Context around the FIRST whitespace char: up to 5 chars on each side, for a "m/a?a =1"
// style hint that helps locate the space in a long URL. index = -1 when there is no space.
export const getSpaceContext = (
  url: string
): { index: number; left: string; right: string } => {
  const index = url.search(WHITESPACE)
  if (index === -1) return { index: -1, left: '', right: '' }
  return {
    index,
    left: url.slice(Math.max(0, index - 5), index),
    right: url.slice(index + 1, index + 6),
  }
}

// Removes every whitespace character from the URL (used by the "Fix" button).
export const stripUrlSpaces = (url: string): string => url.replace(/\s+/g, '')

// Verbal locator for the first whitespace: quotes up to 5 chars on each side (with … where the
// URL continues) so a message reads e.g. `between "…m/a?a" and "=1"`. Empty when there's no space.
export const describeSpaceLocation = (url: string): string => {
  const { index, left, right } = getSpaceContext(url)
  if (index === -1) return ''
  const lead = index - 5 > 0 ? '…' : ''
  const trail = index + 6 < url.length ? '…' : ''
  const before = left ? `"${lead}${left}"` : 'the start'
  const after = right ? `"${right}${trail}"` : 'the end'
  return `between ${before} and ${after}`
}

// react-hook-form validate rule: invalid (blocks the field) when the URL has a space, with a
// message that says where it is. Pair with a UrlSpaceFixButton to offer one-click removal.
export const noSpaceRule = (url?: string): true | string =>
  !urlHasSpace(url) || `URL contains a space ${describeSpaceLocation(url || '')}`

const URL_FORMAT_MESSAGE =
  'Enter a valid URL with http/https and a domain (e.g. https://example.com)'

// react-hook-form validate rule: a valid http(s) URL with a domain (required field).
export const httpUrlRule = (url?: string): true | string =>
  isValidHttpUrlWithDomain(url) || URL_FORMAT_MESSAGE

// Same, but treats an empty value as valid (optional field).
export const optionalHttpUrlRule = (url?: string): true | string =>
  !url || isValidHttpUrlWithDomain(url) || URL_FORMAT_MESSAGE

export const isHttpUrl = (url: string): boolean => /^http:\/\//i.test(url)

export const upgradeToHttps = (url: string): string => url.replace(/^http:\/\//i, 'https://')

export const resolveUrlFix = (
  url: string | undefined,
  requireHttps: boolean,
): { label: string; next: string } | null => {
  if (!url) return null
  if (urlHasSpace(url)) return { label: 'Remove space', next: stripUrlSpaces(url) }
  if (requireHttps && isHttpUrl(url)) return { label: 'Switch to https', next: upgradeToHttps(url) }
  return null
}

const HTTPS_SHAPE_MESSAGE = 'Enter a full URL, for example https://example.com'
const HTTPS_PROTOCOL_MESSAGE = 'URL must use https://'

export const httpsUrlRule = (url?: string): true | string => {
  if (!isValidHttpUrlWithDomain(url)) return HTTPS_SHAPE_MESSAGE
  return isValidHttpsUrlWithDomain(url) || HTTPS_PROTOCOL_MESSAGE
}

export const optionalHttpsUrlRule = (url?: string): true | string => !url || httpsUrlRule(url)

// The API caps targetValue at 1,000,000,000 (CON-538, MAX_TARGET_VALUE). The ceiling sits below
// every Epom limit; above it Epom answers the campaign sync with a 400, which deactivates the
// campaign. Mirror the cap here so the form fails fast instead of failing on Save.
export const TARGET_VALUE_MAX = 1_000_000_000

// Danish grouping ("1.000.000.000"), matching the app's audience and the comma decimal separator
// on the input itself (InputNumber.tsx). The locale is explicit rather than the runtime default
// because the message is asserted in tests.
const TARGET_VALUE_RANGE_MESSAGE =
  `Must be greater than 0 and no more than ${TARGET_VALUE_MAX.toLocaleString('da-DK')}`

// react-hook-form validate rule for the campaign goal value. The lower bound is EXCLUSIVE: the API
// rejects `<= 0` for every real target type, so accepting 0 here would only defer the failure to
// Save. Absent values are valid — presence is a separate rule, and the field is legitimately empty
// for non-BUDGET target types.
export const targetValueRangeRule = (value?: number | string | null): true | string => {
  if (value === undefined || value === null || value === '') {
    return true
  }

  const num = Number(value)

  return (Number.isFinite(num) && num > 0 && num <= TARGET_VALUE_MAX) || TARGET_VALUE_RANGE_MESSAGE
}

// Mirrors COUNT_BASED_TARGET_TYPES in the API's IsValidTargetValue: these goals are counts of
// whole things, so the API rejects decimals. BUDGET and CPM may be fractional.
const COUNT_BASED_TARGET_TYPES = [
  CampaignTargetType.Impressions,
  CampaignTargetType.Reads,
  CampaignTargetType.Clickouts,
]

const WHOLE_NUMBER_MESSAGE = 'Must be a whole number'

export const targetValueWholeNumberRule = (
  value?: number | string | null,
  targetType?: CampaignTargetType | null,
): true | string => {
  if (value === undefined || value === null || value === '') {
    return true
  }

  if (!targetType || !COUNT_BASED_TARGET_TYPES.includes(targetType)) {
    return true
  }

  const num = Number(value)

  // Non-finite input is targetValueRangeRule's business — don't report it twice.
  return !Number.isFinite(num) || Number.isInteger(num) || WHOLE_NUMBER_MESSAGE
}

export const DAILY_BUDGET_LIMIT_MAX = 1_000_000_000

const DAILY_BUDGET_LIMIT_RANGE_MESSAGE =
  `Must be between 0 and ${DAILY_BUDGET_LIMIT_MAX.toLocaleString('da-DK')}`

export const dailyBudgetLimitRangeRule = (value?: number | string | null): true | string => {
  if (value === undefined || value === null || value === '') {
    return true
  }

  const num = Number(value)

  return (
    (Number.isFinite(num) && num >= 0 && num <= DAILY_BUDGET_LIMIT_MAX) ||
    DAILY_BUDGET_LIMIT_RANGE_MESSAGE
  )
}

// CON-697. Advisory only: these return a message or null and are NOT `validate` rules. Never pass
// them to `register` or a Controller's `rules`. A failure there disables Save, and this is advice.

export const TEASER_HEADLINE_TOOLTIP =
  'Publishers prefer headlines without full stops. Use colons or dashes to separate ideas.'

export const ARTICLE_HEADLINE_TOOLTIP =
  'Publishers prefer headlines without commas or full stops. Use colons or dashes to separate ideas.'

const TEASER_HEADLINE_WARNING =
  'Avoid full stops in headlines. Use a colon or dash to connect ideas.'

const ARTICLE_HEADLINE_WARNING =
  "Headlines usually don't include commas or full stops. Try a colon or dash instead."

const DIGIT = /\d/

// Exempt marks that are not sentence punctuation: between two digits is Danish number formatting
// ("1.000 kr.", "3,5 mio."), and a repeated mark is an ellipsis. Abbreviations are still flagged.
const hasSentencePunctuation = (text: string, marks: string): boolean => {
  for (let i = 0; i < text.length; i++) {
    const mark = text[i]
    if (!marks.includes(mark)) continue
    if (DIGIT.test(text[i - 1] ?? '') && DIGIT.test(text[i + 1] ?? '')) continue
    if (text[i - 1] === mark || text[i + 1] === mark) continue
    return true
  }
  return false
}

export const teaserHeadlineWarning = (headline?: string | null): string | null =>
  hasSentencePunctuation(headline ?? '', '.') ? TEASER_HEADLINE_WARNING : null

export const articleHeadlineWarning = (headline?: string | null): string | null =>
  hasSentencePunctuation(headline ?? '', '.,') ? ARTICLE_HEADLINE_WARNING : null
